import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import tPaths from './tPaths';
import { ESystemUserPermission } from '../types/enums/ESystemUserPermission';
import { ISystemUserPermissions } from '../types/interfaces/ISystemUserPermissions';

const usePermissionsStatus = () => {
  const { t } = useTranslation();

  const permissionsStatusItems = useMemo(
    () => [
      {
        fieldName: 'packages',
        label: t(tPaths.sideMenu.packages),
        prefix: 'packages',
      },
      {
        fieldName: 'customers',
        label: t(tPaths.sideMenu.customers),
        prefix: 'customers',
      },
      {
        fieldName: 'systemUsers',
        label: t(tPaths.sideMenu.systemUsers),
        prefix: 'system_users',
      },
      {
        fieldName: 'carrierStatus',
        label: t(tPaths.sideMenu.carrierStatus),
        prefix: 'carrier_status',
      },
      {
        fieldName: 'notifications',
        label: t(tPaths.sideMenu.notifications),
        prefix: 'notifications',
      },
      {
        fieldName: 'appSettings',
        label: t(tPaths.sideMenu.settings),
        prefix: 'app_settings',
      },
      {
        fieldName: 'promocodes',
        label: t(tPaths.sideMenu.promoCodes),
        prefix: 'promocodes',
      },
      {
        fieldName: 'promoCampaigns',
        label: t(tPaths.sideMenu.promoCampaigns),
        prefix: 'promocampaigns',
      },
      // TODO: uncomment in MVP 2
      // {
      //   fieldName: 'calculator',
      //   label: t(tPaths.sideMenu.calculator),
      //   prefix: 'calculator',
      // },
    ],
    [t],
  );

  const permissionsToArray = useCallback(
    (permissions) => {
      return permissions
        ? permissionsStatusItems.reduce((acc, cv) => {
            const permissionStatus = [];
            if (permissions[cv.fieldName].view) {
              permissionStatus.push(`${cv.prefix}_view`);
            }
            if (permissions[cv.fieldName].edit) {
              permissionStatus.push(`${cv.prefix}_edit`);
            }
            return [...acc, ...permissionStatus];
          }, [] as string[])
        : [];
    },

    [permissionsStatusItems],
  );

  const arrayToPermissions = useCallback((permissions: string[]): ISystemUserPermissions => {
    const hasPermission = (permission: ESystemUserPermission) => permissions.includes(permission);

    return {
      packages: {
        view: hasPermission(ESystemUserPermission.PackagesView),
        edit: hasPermission(ESystemUserPermission.PackagesEdit),
      },
      customers: {
        view: hasPermission(ESystemUserPermission.CustomersView),
        edit: hasPermission(ESystemUserPermission.CustomersEdit),
      },
      systemUsers: {
        view: hasPermission(ESystemUserPermission.SystemUsersView),
        edit: hasPermission(ESystemUserPermission.SystemUsersEdit),
      },
      promoCampaigns: {
        view: hasPermission(ESystemUserPermission.PromoCampaignsView),
        edit: hasPermission(ESystemUserPermission.PromoCampaignsEdit),
      },
      carrierStatus: {
        view: hasPermission(ESystemUserPermission.CarrierStatusView),
        edit: hasPermission(ESystemUserPermission.CarrierStatusEdit),
      },
      notifications: {
        view: hasPermission(ESystemUserPermission.NotificationsView),
        edit: hasPermission(ESystemUserPermission.NotificationsEdit),
      },
      appSettings: {
        view: hasPermission(ESystemUserPermission.AppSettingsView),
        edit: hasPermission(ESystemUserPermission.AppSettingsEdit),
      },
      promocodes: {
        view: hasPermission(ESystemUserPermission.PromocodesView),
        edit: hasPermission(ESystemUserPermission.PromocodesEdit),
      },
      // TODO: uncomment in MVP 2
      // calculator: {
      //   view: hasPermission(ESystemUserPermission.CalculatorView),
      //   edit: hasPermission(ESystemUserPermission.CalculatorEdit),
      // },
    };
  }, []);
  return {
    permissionsStatusItems,
    permissionsToArray,
    arrayToPermissions,
  };
};

export default usePermissionsStatus;
